export interface RuntimeConfig {
  apiBaseUrl: string;
  mockRoutes: string[];
  environment: string;
  featureFlags: Record<string, boolean>;
  openAiLogsEnabled: boolean;
  awsAdaptorBaseUrl?: string;
  awsTelemetryTemplateUrl?: string;
  awsTelemetryDefaultRegion?: string;
}

type RuntimeConfigPayload = Partial<RuntimeConfig>;

const MOCK_ALIAS = new Map<string, string[]>([
  ['all', ['auth', 'orgs', 'org-console', 'dashboards', 'signals', 'metrics', 'flows', 'topology-runtime-overlays']],
  ['none', []]
]);

const DEFAULT_API_BASE_URL = resolveDefaultApiBaseUrl();
const DEFAULT_MOCK_ROUTES = parseMockRoutes(import.meta.env.VITE_MOCK_ROUTES ?? 'all');
const DEFAULT_ENVIRONMENT = import.meta.env.MODE ?? 'development';

export const createDefaultConfig = (): RuntimeConfig => ({
  apiBaseUrl: DEFAULT_API_BASE_URL,
  mockRoutes: DEFAULT_MOCK_ROUTES,
  environment: DEFAULT_ENVIRONMENT,
  featureFlags: {},
  openAiLogsEnabled: parseOptionalBoolean(import.meta.env.VITE_OPENAI_LOGS_ENABLED) ?? false,
  awsAdaptorBaseUrl: import.meta.env.VITE_AWS_ADAPTOR_BASE_URL as string | undefined,
  awsTelemetryTemplateUrl: import.meta.env.VITE_AWS_TELEMETRY_TEMPLATE_URL as string | undefined,
  awsTelemetryDefaultRegion: (import.meta.env.VITE_AWS_TELEMETRY_DEFAULT_REGION as string | undefined) ?? 'us-east-1'
});

export async function loadRuntimeConfig(fallback: RuntimeConfig = createDefaultConfig()): Promise<RuntimeConfig> {
  for (const candidate of resolveRuntimeConfigCandidates()) {
    try {
      const response = await fetch(candidate, { cache: 'no-store' });
      if (!response.ok) {
        continue;
      }

      const payload = (await response.json()) as RuntimeConfigPayload;
      return mergeConfig(fallback, payload);
    } catch (error) {
      console.warn('[runtime-config] Candidate failed', candidate, error);
    }
  }

  return fallback;
}

function mergeConfig(base: RuntimeConfig, payload: RuntimeConfigPayload): RuntimeConfig {
  return {
    apiBaseUrl:
      typeof payload.apiBaseUrl === 'string' && payload.apiBaseUrl.trim().length > 0
        ? normalizeApiBaseUrl(payload.apiBaseUrl, base.apiBaseUrl)
        : base.apiBaseUrl,
    mockRoutes: Array.isArray(payload.mockRoutes) ? parseMockRoutes(payload.mockRoutes.join(',')) : base.mockRoutes,
    environment: typeof payload.environment === 'string' && payload.environment.trim().length > 0 ? payload.environment : base.environment,
    featureFlags: typeof payload.featureFlags === 'object' && payload.featureFlags != null ? payload.featureFlags : base.featureFlags,
    openAiLogsEnabled: typeof payload.openAiLogsEnabled === 'boolean' ? payload.openAiLogsEnabled : base.openAiLogsEnabled,
    awsAdaptorBaseUrl:
      typeof payload.awsAdaptorBaseUrl === 'string' && payload.awsAdaptorBaseUrl.trim().length > 0
        ? payload.awsAdaptorBaseUrl.trim()
        : base.awsAdaptorBaseUrl,
    awsTelemetryTemplateUrl:
      typeof payload.awsTelemetryTemplateUrl === 'string' && payload.awsTelemetryTemplateUrl.trim().length > 0
        ? payload.awsTelemetryTemplateUrl.trim()
        : base.awsTelemetryTemplateUrl,
    awsTelemetryDefaultRegion:
      typeof payload.awsTelemetryDefaultRegion === 'string' && payload.awsTelemetryDefaultRegion.trim().length > 0
        ? payload.awsTelemetryDefaultRegion.trim()
        : base.awsTelemetryDefaultRegion
  };
}

export function parseMockRoutes(value: string): string[] {
  return value
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean)
    .flatMap((token) => {
      if (MOCK_ALIAS.has(token)) {
        return MOCK_ALIAS.get(token)!;
      }
      return token;
    })
    .filter((token, index, arr) => arr.indexOf(token) === index);
}

function resolveDefaultApiBaseUrl(): string {
  const prefixEnv = import.meta.env.VITE_API_BASE_PREFIX as string | undefined;
  const directEnv = import.meta.env.VITE_API_BASE_URL as string | undefined;

  if (prefixEnv) {
    return normalizeApiBaseUrl(prefixEnv, '/public/v1');
  }

  if (directEnv) {
    return normalizeApiBaseUrl(directEnv, '/public/v1');
  }

  return '/public/v1';
}

export function normalizeApiBaseUrl(input: string, fallback: string = '/public/v1'): string {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return fallback;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    const withoutTrailingSlash = trimmed.replace(/\/+$/, '');
    if (withoutTrailingSlash.endsWith('/v1')) {
      return withoutTrailingSlash;
    }
    return `${withoutTrailingSlash}/v1`;
  }

  if (trimmed.startsWith('/')) {
    const cleaned = trimmed.replace(/\/+$/, '');
    return cleaned.length > 0 ? cleaned : fallback;
  }

  return fallback;
}

function parseOptionalBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value !== 'string') {
    return undefined;
  }
  const normalized = value.trim().toLowerCase();
  if (normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on') {
    return true;
  }
  if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === 'off') {
    return false;
  }
  return undefined;
}

export function resolveRuntimeConfigCandidates(): string[] {
  const candidates: string[] = [];

  if (typeof document !== 'undefined' && document.baseURI) {
    candidates.push(new URL('runtime-config.json', document.baseURI).toString());
  }

  if (typeof window !== 'undefined') {
    candidates.push(new URL('/runtime-config.json', window.location.origin).toString());
  } else {
    candidates.push('/runtime-config.json');
  }

  return candidates.filter((candidate, index, items) => items.indexOf(candidate) === index);
}
